import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "api/baseurl";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Grid,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Box,
  Typography,
  Card,
  Autocomplete,
  TextField,
} from "@mui/material";
import { keyframes } from "@mui/system";

// Custom components
import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

// Encryption utils
import { encryptBody } from "encryptionAndDecryption/bodyEncryption";
import { decryptBody } from "encryptionAndDecryption/bodyDecryption";
import { fetchSecretKey } from "encryptionAndDecryption/secretKeyDecryption";
import MedicalAbstractBackground from "./MedicalAbstractBackground ";

// Dummy Custom Stepper Component
const CustomStepper = ({ title }) => {
  return (
    <Box mb={3} display="flex" justifyContent="center">
      <Typography
        variant="h6"
        sx={{ fontWeight: "bold", color: "#2e7fae", textAlign: "center" }}
      >
        {title}
      </Typography>
    </Box>
  );
};

function CreateDoctorPatientMapping() {
  const loginData = JSON.parse(localStorage.getItem("loginData"));
  const userid = loginData?.userid;

  const date = new Date();
  let day = date.getDate().toString().padStart(2, "0");
  let month = (date.getMonth() + 1).toString().padStart(2, "0");
  let year = date.getFullYear();
  let currentDate = `${year}-${month}-${day}`;

  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const [patients, setPatients] = useState([]);
  const [patientInput, setPatientInput] = useState("");

  const [doctorTypes, setDoctorTypes] = useState([]);
  const [doctorNames, setDoctorNames] = useState([]);
  const [doctorInput, setDoctorInput] = useState("");

  const [mappingDetails, setMappingDetails] = useState({
    ccid: userid,
    date: currentDate,
    patient: null, 
    patientid: "",
    doctorType: "",
    doctorid: [],
  });

  // 🔹 Fetch patients (with or without search)
const fetchPatients = async (searchTerm = "") => {

    console.log("🔍 Fetching Patients...", { searchTerm });

  setLoading(true);
  try {
    
    const secretKey = await fetchSecretKey();
    const token = JSON.parse(localStorage.getItem("loginData"))?.token;

    let response;
      if (searchTerm.trim()) {
      response = await axios.post(
        `/api/patientregistration/searchByCcid/${userid}`,
        { searchTerm },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } else {
      response = await axios.get(
        `/api/patientregistration/ccid/nopagination/${userid}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
    }

   if (response.status === 200) {
  const decrypted = decryptBody(response.data, secretKey);
  console.log("👨‍⚕️ Raw Patients Response:", response.data);
  console.log("🔓 Decrypted Patients:", decrypted);

  let patientsArray = [];

  if (Array.isArray(decrypted)) {
    patientsArray = decrypted; // no-pagination API
  } else if (decrypted?.content && Array.isArray(decrypted.content)) {
    patientsArray = decrypted.content; // search API
  }

  console.log("✅ Patients Array Length:", patientsArray.length);
  setPatients(patientsArray);
}
  } catch (err) {
    console.error("Error fetching patients:", err);
  } finally {
    setLoading(false);
  }
};



  // 🔹 Fetch doctor types
  const fetchDoctorTypes = async () => {
    try {
      const secretKey = await fetchSecretKey();
      const token = JSON.parse(localStorage.getItem("loginData"))?.token;
      const response = await axios.get("/api/doctortype", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.status === 200) {
        const decrypted = decryptBody(response.data, secretKey);
        setDoctorTypes(decrypted || []);
      }
    } catch (err) {
      console.error("Error fetching doctor types:", err);
    }
  };

  // 🔹 Fetch doctor names (with or without search)
  const fetchDoctorNames = async (doctorType, searchTerm = "") => {
    if (!doctorType) return;
    setLoading(true);
    try {
      const secretKey = await fetchSecretKey();
      const token = JSON.parse(localStorage.getItem("loginData"))?.token;

      let response;
      if (searchTerm.trim()) {
        response = await axios.post(
          `/api/doctorregistration/searchByCcid/${userid}`,
          { searchTerm }, 
    { headers: { Authorization: `Bearer ${token}` } }
  );
      } else {
        response = await axios.get(
          `/api/doctorregistration/doctorType/${doctorType}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      if (response.status === 200) {
        const decrypted = decryptBody(response.data, secretKey);
  setDoctorNames(Array.isArray(decrypted) ? decrypted : []);
      }
    } catch (err) {
      console.error("Error fetching doctors:", err);
    } finally {
      setLoading(false);
    }
  };
useEffect(() => {
  fetchPatients();
  fetchDoctorTypes();
}, []);


useEffect(() => {
  const delayDebounceFn = setTimeout(() => {
    if (patientInput.trim()) {
      fetchPatients(patientInput);
    } else {
      fetchPatients("");
    }
  }, 500);

  return () => clearTimeout(delayDebounceFn);
}, [patientInput]);



  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      fetchDoctorNames(mappingDetails.doctorType, doctorInput);
    }, 500);
    return () => clearTimeout(delayDebounce);
  }, [doctorInput]);

  // 🔹 Handle checkbox toggle
  const handleDoctorCheckbox = (id) => {
    setMappingDetails((prev) => {
      const alreadySelected = prev.doctorid.includes(id);
      return {
        ...prev,
        doctorid: alreadySelected
          ? prev.doctorid.filter((docId) => docId !== id)
          : [...prev.doctorid, id],
      };
    });
  };

  // 🔹 Submit mapping
const handleSubmit = async (e) => {
    e.preventDefault();
    if (!mappingDetails.patientid) {
      toast.error("Please select a patient.");
      return;
    }
    if (!mappingDetails.doctorid || mappingDetails.doctorid.length === 0) {
      toast.error("Please select at least one doctor.");
      return;
    }

    setLoading(true);
    try {
      const secretKey = await fetchSecretKey();
      const token = JSON.parse(localStorage.getItem("loginData"))?.token;

      const mappingList = mappingDetails.doctorid.map((docId) => ({
        ccid: userid,
        date: currentDate,
        patientid: mappingDetails.patient.id,
        doctorid: docId,
      }));

      const rawPayload = { mapping: mappingList };
      const encryptedData = encryptBody(rawPayload, secretKey);

      const response = await axios.post(
        "/api/patientdoctormapping/create",
        encryptedData,
        {
          headers: {
            "Content-Type": "text/plain",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200) {
        const decrypted = decryptBody(response.data, secretKey);
        toast.success(decrypted?.message || "Mapping saved successfully!");
        navigate(-1);
      }
    } catch (err) {
        console.error("Error saving mapping:", err);
     toast.error(err?.response?.data?.message);
     } finally {
       setLoading(false);
     }
   };

  const spinAnimation = keyframes`
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  `;
  const dotPositions = [
    { top: "5%", left: "50%" },
    { top: "20%", left: "80%" },
    { top: "50%", left: "95%" },
    { top: "80%", left: "80%" },
    { top: "95%", left: "50%" },
    { top: "80%", left: "20%" },
    { top: "50%", left: "5%" },
    { top: "20%", left: "20%" },
  ];


  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MedicalAbstractBackground/>
      {loading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            zIndex: 9999,
          }}
        >
          <Box
            sx={{
              position: "relative",
              width: 80,
              height: 80,
              animation: `${spinAnimation} 1.5s linear infinite`,
            }}
          >
            {dotPositions.map((pos, index) => (
              <Box
                key={index}
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: "#1A237E",
                  position: "absolute",
                  ...pos,
                }}
              />
            ))}
          </Box>
        </Box>
      )}

      <Card sx={{ width: "1000px", maxWidth: "1200px", padding: 3, margin: "auto" }}>
        <MDBox pt={4} pb={3} px={3}>
          {/* Stepper Title */}
          <CustomStepper  sx={{ color: "#3949AB", fontWeight: 600 }}title="Doctor - Patient Mapping" />

          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              {/* 🔹 Patient Dropdown with Search */}
              <Grid item xs={6}>
          <Autocomplete
  options={Array.isArray(patients) ? patients : []}
  getOptionLabel={(option) =>
    option ? `${option.firstname} ${option.lastname}` : ""
  }
  value={mappingDetails.patient} // whole object
  onChange={(e, newValue) => {
    console.log("🖱️ Selected patient:", newValue);
    setMappingDetails((prev) => ({
      ...prev,
      patient: newValue,           
      patientid: newValue?.id || "",
    }));
  }}
  inputValue={patientInput}
  onInputChange={(e, newInputValue, reason) => {
    console.log(
      "⌨️ Input field updated:",
      newInputValue,
      "| Reason:",
      reason
    );

    if (reason === "input") {
      setPatientInput(newInputValue);
    } else if (reason === "reset") {
      // ✅ Show full selected name
      const fullName =
        newInputValue ||
        (mappingDetails.patient
          ? `${mappingDetails.patient.firstname} ${mappingDetails.patient.lastname}`
          : "");

      console.log("🔄 Reset triggered, setting full name:", fullName);
      setPatientInput(fullName);
    }
  }}
  renderInput={(params) => (
    <TextField {...params} label="Select Patient"
   placeholder="search or select Patient"

    variant="outlined" />
  )}
  freeSolo={false}
/>


              </Grid>

              {/* 🔹 Doctor Type Dropdown */}
              <Grid item xs={6}>
                <TextField
                  select
                  label="Doctor Type"
                  fullWidth
                  value={mappingDetails.doctorType || ""}
                  onChange={(e) => {
                    setMappingDetails({
                      ...mappingDetails,
                      doctorType: e.target.value,
                    });
                    fetchDoctorNames(e.target.value);
                  }}

                   sx={{
      "& .MuiInputBase-root": {
        height: "45px",  
      },
    }}
                >
                  {doctorTypes.map((dt) => (
                    <MenuItem key={dt.doctor_type} value={dt.doctor_type}>
                      {dt.doctor_type}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* 🔹 Doctor Names with Search + Checkboxes */}
             {/* 🔹 Doctor Names with Search + Checkboxes */}
<Grid item xs={12}>
  <Autocomplete
  multiple
  disableCloseOnSelect
  options={Array.isArray(doctorNames) ? doctorNames : []}
  getOptionLabel={(option) =>
    option ? `${option.firstname} ${option.lastname}` : ""
  }
  value={
    Array.isArray(doctorNames)
      ? doctorNames.filter((doc) => mappingDetails.doctorid.includes(doc.id))
      : []
  }
  onChange={(e, newValue) =>
    setMappingDetails({
      ...mappingDetails,
      doctorid: newValue.map((doc) => doc.id),
    })
  }
  inputValue={doctorInput}
  onInputChange={(e, newInputValue) => {
    if (e?.type === "change") {
      // ✅ only update when user types
      setDoctorInput(newInputValue);
    }
  }}
  renderOption={(props, option, { selected }) => (
    <li {...props}>
      <Checkbox checked={selected} />
      {option.firstname} {option.lastname}
    </li>
  )}
  renderInput={(params) => (
    <TextField
      {...params}
      label="Select Doctor"
      variant="outlined"
      placeholder="search or select doctor"
    />
  )}
/>

</Grid>

            </Grid>

            {/* 🔹 Buttons */}
            <MDBox mt={3} display="flex" justifyContent="space-between">
              <MDButton
                variant="outlined"
                onClick={() => navigate(-1)}
                style={{
                  backgroundColor: "#1A237E",
                  color: "#fff",
                  textTransform: "none",
                }}
              >
                Cancel
              </MDButton>

              <MDButton
                type="submit"
                variant="contained"
                style={{
                  backgroundColor: "#1A237E",
                  color: "#fff",
                  textTransform: "none",
                }}
              >
                Submit
              </MDButton>
            </MDBox>
          </form>
        </MDBox>
      </Card>
    </DashboardLayout>
  );
}

export default CreateDoctorPatientMapping;